import React, { useMemo, useState } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import { PieChart, Pie, Cell, ResponsiveContainer, Sector } from "recharts";
import { LuIndianRupee, LuTrophy, LuUsers } from "react-icons/lu";
import BasicBars from "../BarChart/barChart";
import { PayoutDateRangePicker } from "./PayoutDateRangePicker";
import { BORDER, cardSx } from "../AssetListing/assetListingShared";
import {
  DARK_NAVY,
  DARK_NAVY_LIGHT,
  INPUT_BORDER_FOCUS,
  PROFILE_FONT,
} from "../../constants/layout";

const ICON_BLUE = INPUT_BORDER_FOCUS;
const SUCCESS_COLOR = "#08991F";

const CHART_COLORS: Record<string, string> = {
  "Mutual Fund": "#1E3A5F",
  Equity: "#08991F",
  AIF: "#E6B800",
  PMS: "#7B5EA7",
  "Fixed Deposit": "#4A90D9",
  "Unlisted Equity": "#E07A2F",
  Bond: "#5A6B7D",
  PPF: "#8B7355",
  NPS: "#D32F2F",
  Others: "#9E9E9E",
};

const PIE_SLICE_COLORS = [
  "#1E3A5F",
  "#08991F",
  "#E6B800",
  "#7B5EA7",
  "#4A90D9",
  "#E07A2F",
  "#5A6B7D",
  "#8B7355",
  "#D32F2F",
];

const getSliceColor = (label: string, index: number) =>
  CHART_COLORS[label] ?? PIE_SLICE_COLORS[index % PIE_SLICE_COLORS.length];

const formatAmount = (value: number) =>
  `₹ ${value.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatCompactAmount = (value: number) => {
  if (value >= 1e7) {
    return `₹ ${(value / 1e7).toLocaleString("en-IN", {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    })} Cr`;
  }
  if (value >= 1e5) {
    return `₹ ${(value / 1e5).toLocaleString("en-IN", {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    })} L`;
  }
  return formatAmount(value);
};

const LAKH = 100000;

const CurrencyValue = ({
  amount,
  prefix,
}: {
  amount: number;
  prefix?: string;
}) => {
  const useCompact = amount >= LAKH;
  const fullText = prefix ? `${prefix} — ${formatAmount(amount)}` : formatAmount(amount);
  const displayText = prefix
    ? `${prefix} — ${useCompact ? formatCompactAmount(amount) : formatAmount(amount)}`
    : useCompact
      ? formatCompactAmount(amount)
      : formatAmount(amount);

  const text = (
    <Typography
      component="span"
      sx={{
        fontFamily: PROFILE_FONT,
        fontSize: 14,
        fontWeight: 700,
        color: DARK_NAVY,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        display: "block",
        maxWidth: "100%",
      }}
    >
      {displayText}
    </Typography>
  );

  if (!useCompact) {
    return text;
  }

  return (
    <Tooltip title={fullText} arrow placement="top">
      {text}
    </Tooltip>
  );
};

const DONUT_SIZE = 220;
const DONUT_OUTER = 92;

const renderActiveSlice = (props: unknown) => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props as {
    cx: number;
    cy: number;
    innerRadius: number;
    outerRadius: number;
    startAngle: number;
    endAngle: number;
    fill: string;
  };
  return (
    <Sector
      cx={cx}
      cy={cy}
      innerRadius={innerRadius}
      outerRadius={outerRadius + 6}
      startAngle={startAngle}
      endAngle={endAngle}
      fill={fill}
      stroke="#ffffff"
      strokeWidth={2}
    />
  );
};

const SectionCard = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <Box sx={{ ...cardSx, p: { xs: 2, md: 2.5 } }}>
    <Typography
      sx={{
        fontFamily: PROFILE_FONT,
        fontSize: 18,
        fontWeight: 700,
        color: DARK_NAVY,
        mb: 2.5,
      }}
    >
      {title}
    </Typography>
    {children}
  </Box>
);

const HighlightRow = ({
  icon,
  iconBg,
  iconColor,
  label,
  value,
}: {
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
  label: string;
  value: React.ReactNode;
}) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: "10px",
        bgcolor: iconBg,
        color: iconColor,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 12, color: DARK_NAVY_LIGHT, mb: 0.25 }}>
        {label}
      </Typography>
      {typeof value === "string" ? (
        <Typography
          sx={{
            fontFamily: PROFILE_FONT,
            fontSize: 14,
            fontWeight: 700,
            color: DARK_NAVY,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {value}
        </Typography>
      ) : (
        value
      )}
    </Box>
  </Box>
);

const EmptyChartState = ({ message }: { message: string }) => (
  <Box
    sx={{
      minHeight: 280,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      bgcolor: "#FAFAFA",
      borderRadius: "12px",
      border: `1px dashed ${BORDER}`,
    }}
  >
    <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 14, color: DARK_NAVY_LIGHT }}>
      {message}
    </Typography>
  </Box>
);

const AssetDonutChart = ({
  data,
  total,
}: {
  data: Array<{ label: string; value: number; fill: string }>;
  total: number;
}) => {
  const [activeIndex, setActiveIndex] = useState<number | undefined>(undefined);
  const hasMultipleSlices = data.length > 1;

  return (
    <Box
      sx={{
        position: "relative",
        width: DONUT_SIZE,
        height: DONUT_SIZE,
        flexShrink: 0,
        mx: "auto",
      }}
    >
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            dataKey="value"
            nameKey="label"
            cx="50%"
            cy="50%"
            innerRadius={56}
            outerRadius={DONUT_OUTER}
            paddingAngle={hasMultipleSlices ? 2 : 0}
            stroke={hasMultipleSlices ? "#ffffff" : "none"}
            strokeWidth={hasMultipleSlices ? 1 : 0}
            isAnimationActive={false}
            startAngle={90}
            endAngle={-270}
            activeIndex={activeIndex}
            activeShape={renderActiveSlice}
            onMouseEnter={(_, index) => setActiveIndex(index)}
            onMouseLeave={() => setActiveIndex(undefined)}
          >
            {data.map((entry, index) => (
              <Cell key={`${entry.label}-${index}`} fill={entry.fill} stroke={entry.fill} />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          textAlign: "center",
          width: 100,
          pointerEvents: "none",
        }}
      >
        <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 16, fontWeight: 700, color: DARK_NAVY, lineHeight: 1.3 }}>
          {formatCompactAmount(total)}
        </Typography>
        <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 11, color: DARK_NAVY_LIGHT }}>
          Total Brokerage
        </Typography>
      </Box>
    </Box>
  );
};

type PayoutAnalyticsDashboardProps = {
  top20: Array<{ name: string; value: number }>;
  byAsset: Array<{ name: string; value: number }>;
  dateRange: { startDate?: number; endDate?: number };
  onDateRangeChange: (range: { startDate?: number; endDate?: number }) => void;
};

export const PayoutAnalyticsDashboard = ({
  top20,
  byAsset,
  dateRange,
  onDateRangeChange,
}: PayoutAnalyticsDashboardProps) => {
  const top20Total = useMemo(() => top20.reduce((sum, item) => sum + item.value, 0), [top20]);
  const highestPayout = top20[0];

  const pieData = useMemo(
    () =>
      [...byAsset]
        .sort((a, b) => b.value - a.value)
        .map((item, index) => ({
          label: item.name,
          value: item.value,
          fill: getSliceColor(item.name, index),
        })),
    [byAsset]
  );

  const assetTotal = useMemo(() => pieData.reduce((sum, item) => sum + item.value, 0), [pieData]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "stretch", md: "flex-start" },
          justifyContent: "space-between",
          gap: 2,
          mb: 0.5,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontFamily: PROFILE_FONT,
              fontSize: { xs: 24, md: 28 },
              fontWeight: 700,
              color: DARK_NAVY,
              mb: 0.5,
            }}
          >
            Payout Analytics
          </Typography>
          <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 14, color: DARK_NAVY_LIGHT }}>
            Visual insights into partner payouts and earnings by product type
          </Typography>
        </Box>
        <PayoutDateRangePicker
          variant="compact"
          defaultShortcut="1Y"
          startDate={dateRange.startDate}
          endDate={dateRange.endDate}
          onChange={onDateRangeChange}
        />
      </Box>

      <SectionCard title="Top 20 Partner Payout">
        {top20.length === 0 ? (
          <EmptyChartState message="No partner payout data found" />
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "1fr 300px" },
              gap: 2.5,
              alignItems: "stretch",
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <BasicBars
                data={top20}
                color={ICON_BLUE}
                height={360}
                yAxisLabel="Payout Amount (₹)"
              />
            </Box>
            <Box
              sx={{
                border: `1px solid ${BORDER}`,
                borderRadius: "12px",
                bgcolor: "#FAFAFA",
                p: 2,
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <Typography
                sx={{ fontFamily: PROFILE_FONT, fontSize: 14, fontWeight: 700, color: DARK_NAVY }}
              >
                Top Payout Highlights
              </Typography>
              <HighlightRow
                icon={<LuTrophy size={18} />}
                iconBg="rgba(30, 58, 95, 0.1)"
                iconColor={ICON_BLUE}
                label="Highest Payout"
                value={
                  highestPayout ? (
                    <CurrencyValue amount={highestPayout.value} prefix={highestPayout.name} />
                  ) : (
                    "—"
                  )
                }
              />
              <HighlightRow
                icon={<LuIndianRupee size={18} />}
                iconBg="rgba(8, 153, 31, 0.1)"
                iconColor={SUCCESS_COLOR}
                label="Total Top 20 Payout"
                value={<CurrencyValue amount={top20Total} />}
              />
              <HighlightRow
                icon={<LuUsers size={18} />}
                iconBg="rgba(123, 94, 167, 0.12)"
                iconColor="#7B5EA7"
                label="Total Partners"
                value={`${top20.length} Partners`}
              />
            </Box>
          </Box>
        )}
      </SectionCard>

      <SectionCard title="Amount by Asset Type">
        {byAsset.length === 0 ? (
          <EmptyChartState message="No asset type data found" />
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "1.2fr auto 280px" },
              gap: { xs: 2.5, lg: 2 },
              alignItems: "center",
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <BasicBars
                data={pieData.map((item) => ({ name: item.label, value: item.value }))}
                color={SUCCESS_COLOR}
                height={340}
                yAxisLabel="Brokerage Amount (₹)"
              />
            </Box>

            <AssetDonutChart data={pieData} total={assetTotal} />

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1.25,
                maxHeight: 340,
                overflowY: "auto",
                pr: 0.5,
              }}
            >
              {pieData.map((item) => {
                const pct = assetTotal > 0 ? ((item.value / assetTotal) * 100).toFixed(1) : "0.0";
                return (
                  <Box
                    key={item.label}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      py: 0.75,
                      borderBottom: `1px solid ${BORDER}`,
                      "&:last-child": { borderBottom: "none" },
                    }}
                  >
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        bgcolor: item.fill,
                        flexShrink: 0,
                      }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontFamily: PROFILE_FONT,
                          fontSize: 13,
                          fontWeight: 600,
                          color: DARK_NAVY,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {item.label}
                      </Typography>
                      <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 12, color: DARK_NAVY_LIGHT }}>
                        {item.value >= LAKH ? (
                          <Tooltip title={formatAmount(item.value)} arrow placement="top">
                            <Box component="span">{formatCompactAmount(item.value)}</Box>
                          </Tooltip>
                        ) : (
                          formatAmount(item.value)
                        )}
                      </Typography>
                    </Box>
                    <Typography
                      sx={{
                        fontFamily: PROFILE_FONT,
                        fontSize: 13,
                        fontWeight: 600,
                        color: ICON_BLUE,
                        flexShrink: 0,
                      }}
                    >
                      {pct}%
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </SectionCard>
    </Box>
  );
};

export default PayoutAnalyticsDashboard;
